// Generated by GitHub Copilot
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Button, Row, Col } from "react-bootstrap";
import {
  FaChevronRight,
  FaChevronDown,
  FaEdit,
  FaCheckCircle,
} from "react-icons/fa";
import { formatTimeAgo } from "../../utils/dateHelpers";
import { getStatusColor } from "../../constants/impactConstants";
import styles from "./ImpactTableRow.module.css";

/**
 * ImpactTableRow - Expandable row for impact display in table view
 * Collapsed: Shows status, workload, description preview (first 25 chars), brand
 * Expanded: Shows CI, full description, incident ID, created metadata, and action buttons
 */
function ImpactTableRow({
  impact,
  currentUserEmail,
  onEdit,
  onResolve,
  expanded,
  onToggleExpand,
}) {
  const statusColors = getStatusColor(impact.status);
  const ChevronIcon = expanded ? FaChevronDown : FaChevronRight;
  // Generated by GitHub Copilot - Case-insensitive email comparison for highlighting
  const isOwnImpact =
    currentUserEmail &&
    impact.createdBy &&
    currentUserEmail.toLowerCase() === impact.createdBy.toLowerCase();

  // Handle row click (toggle expand)
  const handleRowClick = useCallback(
    (e) => {
      // Don't toggle if clicking on buttons
      if (e.target.closest("button")) {
        return;
      }
      onToggleExpand();
    },
    [onToggleExpand],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggleExpand();
      }
    },
    [onToggleExpand],
  );

  // Truncate description to first 25 characters for collapsed view
  const descriptionPreview = impact.impactDescription
    ? impact.impactDescription.length > 25
      ? `${impact.impactDescription.substring(0, 25)}...`
      : impact.impactDescription
    : "No description";

  return (
    <div
      className={`${styles.rowContainer} ${expanded ? styles.expanded : ""} ${isOwnImpact ? styles.myImpact : ""}`}
    >
      {/* Collapsed Row - Always Visible */}
      <Row
        className={styles.collapsedRow}
        onClick={handleRowClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`Impact for ${impact.applicationName || "Unknown application"}. Click to ${expanded ? "collapse" : "expand"} details.`}
      >
        <Col xs="auto" className={styles.iconCell}>
          <ChevronIcon className={styles.chevronIcon} aria-hidden="true" />
        </Col>
        <Col xs="auto" className={styles.statusCell}>
          <span
            className={styles.statusBadge}
            style={{
              backgroundColor: statusColors.bg,
              color: statusColors.text,
            }}
          >
            {impact.status
              ? impact.status.charAt(0).toUpperCase() + impact.status.slice(1)
              : "Unknown"}
          </span>
        </Col>
        <Col xs="auto" className={styles.brandCell}>
          {impact.brand || "Unknown"}
        </Col>
        <Col className={styles.workloadCell}>
          {impact.applicationName || "Unknown"}
        </Col>
        <Col className={styles.descriptionPreviewCell}>
          {descriptionPreview}
        </Col>
      </Row>

      {/* Expanded Content - Shows when expanded is true */}
      {expanded && (
        <div className={styles.expandedContent}>
          {/* CI (Configuration Item) */}
          {impact.ci && (
            <Row className={styles.detailRow}>
              <Col xs={12}>
                <strong>CI:</strong> {impact.ci}
              </Col>
            </Row>
          )}

          {/* Description */}
          <Row className={styles.detailRow}>
            <Col xs={12}>
              <strong>Description:</strong>
              <span className={styles.description}>
                {impact.impactDescription || "No description provided"}
              </span>
            </Col>
          </Row>

          <Row className={styles.detailRow}>
            <Col xs={12}>
              <strong>Incident ID:</strong>
              <span className={styles.incidentCell}>
                {impact.childIncidentNumber || "N/A"}
              </span>
            </Col>
          </Row>

          {/* Created metadata */}
          <Row className={styles.metadataRow}>
            <Col xs={12}>
              <small className={styles.timestamp}>
                Created {formatTimeAgo(impact.createdAt)}
                {impact.createdBy && ` by ${impact.createdBy.split("@")[0]}`}
              </small>
            </Col>
          </Row>

          {/* Action Buttons - Backend enforces permissions */}
          <Row className={`${styles.actionsRow} g-2`}>
            <Col xs="auto">
              <Button
                variant="primary"
                size="md"
                onClick={() => onEdit(impact)}
                title="Edit impact"
                aria-label="Edit impact report"
                className="d-inline-flex align-items-center"
              >
                <FaEdit className="me-1" />
                Edit
              </Button>
            </Col>
            {impact.status === "active" && (
              <Col xs="auto">
                <Button
                  variant="success"
                  size="md"
                  onClick={() => onResolve(impact)}
                  title="Mark as resolved"
                  aria-label="Mark impact as resolved"
                  className="d-inline-flex align-items-center"
                >
                  <FaCheckCircle className="me-1" />
                  Resolve
                </Button>
              </Col>
            )}
          </Row>
        </div>
      )}
    </div>
  );
}

ImpactTableRow.propTypes = {
  impact: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
    applicationName: PropTypes.string,
    brand: PropTypes.string,
    ci: PropTypes.string,
    impactDescription: PropTypes.string,
    childIncidentNumber: PropTypes.string,
    createdAt: PropTypes.string,
    createdBy: PropTypes.string,
  }).isRequired,
  currentUserEmail: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
};

// Wrap in React.memo to prevent unnecessary re-renders when only other rows change
export default React.memo(ImpactTableRow);
